import { Fragment, useContext, useEffect, useState } from "react";
import { QuestionProps } from "./Props";

import { useToast } from "@aidkitorg/component-library";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from "@heroicons/react/24/solid";
import InterfaceContext from "../Context";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import { useLocalizedStrings } from "../Localization";
import { languageContent } from "../Util";
import { generateAccessibleLabel } from "../utils/generateAccessibleLabel";

type Option = {
  id: string;
  text: string;
};

// Utility function to filter options based on query word array
function filterByQuery<T extends Option>(
  query: string[] = [],
  allOptions: T[] = [],
  maxOptions = 20,
  selectedOptionId: string
) {
  const options: T[] = [];
  if (query.length > 0) {
    for (let option of allOptions) {
      // if each space-separated query word matches the option, add to options
      const optionText = option.text.toLowerCase();
      if (query.every((word) => optionText.includes(word))) {
        if (option.id === selectedOptionId) {
          options.unshift(option);
        } else {
          options.push(option);
        }
      }
      // stop when the options list is full
      if (options.length >= maxOptions) break;
    }
  }
  return options;
}

export function SearchSelectQuestion(props: QuestionProps) {
  const context = useContext(InterfaceContext);
  const L = useLocalizedStrings();
  const { toast } = useToast();
  const targetField = props["Target Field"]!;

  const [query, setQuery] = useState("");

  const [allOptions, setAllOptions] = useState<Option[]>([]);
  const [displayedOptions, setDisplayedOptions] = useState<Option[]>([]);
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);

  const { search_type = {}, max_options } = JSON.parse(props['Metadata'] || '{}');
  const { optionsFileUrl = {} } = search_type;

  // Fetch options on mount and when language changes
  useEffect(() => {
    if (!optionsFileUrl) return;

    const fetchData = async () => {
      try {
        // if no options file is specified for the user's language, use the English one
        const url = optionsFileUrl[context.lang] || optionsFileUrl['en'];
        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
        const options: Option[] = await res.json();
        
        if (!Array.isArray(options)) {
          console.log('Found options that are not an array.');
          toast({
            description: L.questions.search_select.options_error,
            variant: 'error'
          });
          return;
        }
        
        setAllOptions(options);
        const optionId = props.info[targetField];
        const option = options?.find(o => o.id === optionId);
        setSelectedOption(option || null);
      } catch (err) {
        console.log(err);
        toast({
          description: L.questions.search_select.options_error,
          variant: 'error'
        });
      }
    };

    fetchData();
  }, [context.lang]);

  // Update displayed options when query changes
  useEffect(() => {
    const queryWords = query
      .trim()
      .toLowerCase()
      .split(" ")
      .filter(word => word);

    setDisplayedOptions(
      queryWords.length === 0
      // We want options to be displayable without a query but let's limit the number
      // because too many displayed options can slow down or crash the browser
        ? allOptions.slice(0, max_options || 20)
        : filterByQuery(queryWords, allOptions, max_options, selectedOption?.id || '')
    );
  }, [query, selectedOption, allOptions]);

  const formattedContent = useModularMarkdown({
    content: props[languageContent(context.lang)] || '',
    info: props.info
  });

  if ((props['Additional Options'] || []).indexOf('Hidden') !== -1) {
    return <span></span>;
  }

  const accessibleLabel = generateAccessibleLabel(props[languageContent(context.lang)])

  return (
    <fieldset>
      <legend>
        {formattedContent}
      </legend>

      {/* Search Input */}
      <Combobox
        disabled={props.blockQuestionEdits}
        value={selectedOption}
        onChange={(option) => {
          props.setInfoKey(targetField, option?.id || '', !!option, false);
          setSelectedOption(option || null)
        }}
        nullable
      >
        <div className="relative">
          <Combobox.Input
            aria-label={accessibleLabel}
            className={`w-full block border-solid py-2 pl-2.5 pr-[3.25rem] rounded-md border-2 focus:ring-gray-400 focus:border-gray-400 truncate ${
              props.info[targetField]
                ? 'border-green-400 ring-green-400'
                : props["Additional Options"]?.includes('Optional')
                  ? 'border-gray-200 ring-gray-200'
                  : 'border-red-200 ring-red-400'
            }`}
            displayValue={(option: Option | null) => option?.text || query}
            onChange={(e: any) => setQuery(e.target.value)}
          />

          {/* Clear Button */}
          {selectedOption?.id && (
            <button
              disabled={props.blockQuestionEdits}
              type="button"
              className="absolute inset-y-0 right-7 flex items-center"
              onClick={() => {
                setSelectedOption(null);
                setQuery('');
                props.setInfoKey(targetField, '', false, false);
              }}
            >
              <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-500 transition" aria-hidden="true" />
            </button>
          )}
  
          {/* Dropdown Button */} 
          <Combobox.Button aria-label={accessibleLabel} className="absolute inset-y-0 right-0 flex items-center pr-1.5">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400 hover:text-gray-500 transition" aria-hidden="true" />
          </Combobox.Button>

          {/* Options List */}
          <Transition
            as={Fragment}
            leave="transition ease-in duration-50"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options
              className="absolute mt-1 max-h-60 w-full overflow-auto bg-white border border-gray-200 rounded-md py-1 text-base shadow-lg focus:outline-none z-50"
            >
              {displayedOptions.length === 0 ? (
                <div className="select-none px-4 py-2">
                  {L.dashboard.no_results}
                </div>
              ) : (
                displayedOptions.map((option: Option) => (
                  <Combobox.Option
                    key={option.id}
                    className={({ active }) =>
                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                        active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                      }`
                    }
                    value={option}
                  >
                    {({ selected, active }) => (
                      <>
                        <span
                          className={`block transition-[max-height] duration-300 ease-in-out ${
                            selected ? 'font-medium' : 'font-normal'
                          } ${
                            active
                              ? 'max-h-48 overflow-visible whitespace-normal text-clip'
                              : 'max-h-6 overflow-hidden text-ellipsis whitespace-nowrap'
                          }`}
                        > 
                          {option.text}
                        </span>
                        {selected && (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                              active ? 'text-white' : 'text-indigo-600'
                            }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </fieldset>
  );
}